'use client';

import { useMemo, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { getCollection, getPieceBalance, exchangePiece } from '@/api/feedApi';
import { getAssetUrl } from '@/utils/assetUrl';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { useAppLanguage, useTranslator } from '@/lang';
import { Icon } from '@/components/icons';
import { usePortalStore } from '@/stores/portalStore';
import { SAFE_AREA_TOP } from '@/contexts/SafeAreaContext';
import {
  TEMP_FG_PRIMARY,
  TEMP_FG_PRIMARY_LOW,
  TEMP_FG_ACCENT,
  TEMP_FG_TROPHY,
  TEMP_BG_INTIMACY_HIGH,
} from '@/constants/tempColors';
import {
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_BOLD_XL,
  TEMP_TEXT_BOLD_2XS,
  TEMP_TEXT_BOLD_XXS,
  TEMP_TEXT_SEMIBOLD_M,
  TEMP_TEXT_SEMIBOLD_XS,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_SEMIBOLD_L,
} from '@/constants/tempTypo';
import type { CollectionAssetDto, INTIMACY_CONDITION } from '@/shared/types/api/Api';

/** 등급 우선순위: L → U → S → R → N */
const GRADE_ORDER: Record<string, number> = { L: 0, U: 1, S: 2, R: 3, N: 4 };

/** 등급별 조각 비용 */
const PIECE_COST_BY_GRADE: Record<string, number> = { L: 300, U: 100, S: 40 };

function getIntimacyLevel(condition: INTIMACY_CONDITION | string): number {
  const m = condition.match(/LEVEL(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

function getVideoPosterPath(assetPath?: string | null): string {
  if (!assetPath) return '';
  const lastDot = assetPath.lastIndexOf('.');
  const basePath = lastDot > 0 ? assetPath.substring(0, lastDot) : assetPath;
  return `${basePath}_poster.webp`;
}

function sortAssets(assets: CollectionAssetDto[]): CollectionAssetDto[] {
  return [...assets].sort((a, b) => {
    const aCollected = a.isOwned || a.isUnlocked ? 0 : 1;
    const bCollected = b.isOwned || b.isUnlocked ? 0 : 1;
    if (aCollected !== bCollected) return aCollected - bCollected;
    return (GRADE_ORDER[a.grade] ?? 99) - (GRADE_ORDER[b.grade] ?? 99);
  });
}

const GRADE_BORDER: Record<string, { gradient: string; shadow?: string }> = {
  L: { gradient: 'linear-gradient(180deg, #FFE76F 0%, #8000FF 100%)', shadow: '0 0 10px rgba(128,0,255,0.5)' },
  U: { gradient: 'linear-gradient(180deg, #DDB139 0%, #462300 100%)' },
  S: { gradient: 'linear-gradient(180deg, #DADADA 0%, #5A5A5A 100%)' },
  R: { gradient: 'linear-gradient(180deg, #4777FF 0%, #0F46E4 100%)' },
  N: { gradient: 'linear-gradient(180deg, #834D10 0%, #5F390F 100%)' },
};

function getPieceCost(asset: CollectionAssetDto): number {
  if (asset.pieceCost > 0) return asset.pieceCost;
  return PIECE_COST_BY_GRADE[asset.grade] ?? 0;
}

interface CollectionDetailProps {
  characterId: string;
  onClose: () => void;
}

export default function CollectionDetail({ characterId, onClose }: CollectionDetailProps) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const { language } = useAppLanguage();
  const { t, f } = useTranslator();
  const openConfirm = usePortalStore((s) => s.openConfirm);
  const queryClient = useQueryClient();

  const { data: collection } = useQuery({
    queryKey: ['collection-detail'],
    queryFn: getCollection,
    enabled: isLoggedIn,
  });

  const character = useMemo(
    () => collection?.characters.find((c) => c.characterId === characterId),
    [collection?.characters, characterId],
  );

  const sortedAssets = useMemo(
    () => (character ? sortAssets(character.assets) : []),
    [character],
  );

  const collectedCount = sortedAssets.filter((a) => a.isOwned || a.isUnlocked).length;

  const handleAssetClick = useCallback(
    async (asset: CollectionAssetDto) => {
      const isCollected = asset.isOwned || asset.isUnlocked;
      if (isCollected) return;

      const pieceCost = getPieceCost(asset);
      if (pieceCost <= 0) return;

      // 조각 잔액 조회
      try {
        const balance = await getPieceBalance();

        openConfirm({
          title: t('collection.exchange_title'),
          subtitle: '',
          infoContent: (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', width: '100%' }}>
              {/* 조각 비용 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box
                  component="img"
                  src="/svgs/piece.svg"
                  alt="piece"
                  sx={{ width: 20, height: 20 }}
                />
                <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: '#000000' }}>
                  {f(t('gacha.piece_count'), [pieceCost])}
                </Typography>
              </Box>

              {/* 보유 조각 */}
              <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: 'rgba(0,0,0,0.5)' }}>
                {f(t('collection.piece_balance'), [balance.amount])}
              </Typography>

              {/* 교환 미리보기 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'center' }}>
                {/* 조각 아이콘 + 비용 */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '12px',
                    bgcolor: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src="/svgs/piece.svg"
                    alt="piece"
                    sx={{ width: 48, height: 48 }}
                  />
                  {/* 비용 뱃지 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      borderRadius: '6px',
                      px: '6px',
                      py: '2px',
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#FFFFFF' }}>
                      {pieceCost}
                    </Typography>
                  </Box>
                </Box>

                {/* 화살표 */}
                <Icon name="icChevronRight" size={20} color="rgba(0,0,0,0.3)" />

                {/* 잠긴 에셋 미리보기 */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '12px',
                    bgcolor: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 50%, #2a2a2a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="icLock" size={28} color="rgba(255,255,255,0.3)" />
                  </Box>
                  {/* 미디어 타입 아이콘 */}
                  <Box sx={{ position: 'absolute', bottom: 4, right: 4, display: 'flex', gap: '2px' }}>
                    {asset.assetType === 'VIDEO' ? (
                      <Icon name="icPlayFill" size={12} color="rgba(255,255,255,0.5)" />
                    ) : (
                      <Icon name="icImage" size={12} color="rgba(255,255,255,0.5)" />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          ),
          cancelText: t('gacha.cancel'),
          confirmText: t('gacha.purchase'),
          onConfirm: async () => {
            if (balance.amount < pieceCost) {
              openConfirm({
                title: t('collection.piece_insufficient_title'),
                subtitle: f(t('collection.piece_insufficient_subtitle'), [pieceCost, balance.amount]),
                infoContent: null,
                cancelText: '',
                confirmText: t('gacha.confirm'),
                onConfirm: () => {},
              });
              return;
            }

            try {
              const result = await exchangePiece({ itemId: asset.itemId! });
              if (result.success) {
                // 컬렉션 데이터 갱신
                queryClient.invalidateQueries({ queryKey: ['collection-detail'] });
                queryClient.invalidateQueries({ queryKey: ['collection'] });
                queryClient.invalidateQueries({ queryKey: ['pieceBalance'] });
                openConfirm({
                  title: t('collection.exchange_done_title'),
                  subtitle: t('collection.exchange_done_subtitle'),
                  infoContent: (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                      <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: 'rgba(0,0,0,0.5)' }}>
                        {f(t('collection.piece_remaining'), [result.remainingPieces])}
                      </Typography>
                    </Box>
                  ),
                  cancelText: '',
                  confirmText: t('gacha.confirm'),
                  onConfirm: () => {},
                });
              }
            } catch {
              openConfirm({
                title: t('collection.exchange_failed_title'),
                subtitle: t('collection.try_again'),
                infoContent: null,
                cancelText: '',
                confirmText: t('gacha.confirm'),
                onConfirm: () => {},
              });
            }
          },
        });
      } catch {
        // API 에러 처리
      }
    },
    [openConfirm, queryClient, t, f],
  );

  if (!character) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: '#000000',
          color: '#FFFFFF',
        }}
      >
        <Box
          sx={{
            pt: `calc(12px + ${SAFE_AREA_TOP})`,
            px: '16px',
            pb: '8px',
            flexShrink: 0,
          }}
        >
          <Box
            onClick={onClose}
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Icon name="icClose" size={24} color="#FFFFFF" />
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: '24px',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="icTrophy" size={28} color="rgba(255,255,255,0.5)" />
          </Box>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
            {t('collection.empty_detail')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const name = getLocalizedText(character.characterName, language);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#000000',
        color: '#FFFFFF',
      }}
    >
      {/* Header: X button */}
      <Box
        sx={{
          pt: `calc(12px + ${SAFE_AREA_TOP})`,
          px: '16px',
          pb: '8px',
          flexShrink: 0,
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <Icon name="icClose" size={24} color="#FFFFFF" />
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Profile section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: '8px',
            pb: '24px',
          }}
        >
          {/* Profile image */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid',
              borderColor: '#f8b4d9',
              bgcolor: '#1a1a1a',
              mb: '12px',
            }}
          >
            {character.profileImagePath && (
              <Box
                component="img"
                src={getAssetUrl(character.profileImagePath, 'large')}
                alt={name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>

          {/* Name */}
          <Typography sx={{ ...TEMP_TEXT_BOLD_XL, color: '#FFFFFF', mb: '8px' }}>
            {name}
          </Typography>

          {/* Collection count badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: '20px',
              px: '14px',
              py: '6px',
            }}
          >
            <Icon name="icTrophy" size={18} color={TEMP_FG_TROPHY} />
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: '#FFFFFF' }}>
              {collectedCount} / {character.totalCount}
            </Typography>
          </Box>
        </Box>

        {/* Asset Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            px: '16px',
            pb: '40px',
          }}
        >
          {sortedAssets.map((asset) => (
            <DetailAssetCard
              key={asset.id}
              asset={asset}
              isOfficialMuse={character.isOfficialMuse}
              onLockedClick={handleAssetClick}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

interface DetailAssetCardProps {
  asset: CollectionAssetDto;
  isOfficialMuse: boolean;
  onLockedClick: (asset: CollectionAssetDto) => void;
}

function DetailAssetCard({ asset, isOfficialMuse, onLockedClick }: DetailAssetCardProps) {
  const { t } = useTranslator();
  const isCollected = asset.isOwned || asset.isUnlocked;
  const intimacyLevel = getIntimacyLevel(asset.intimacyCondition);
  const isIntimacyType = intimacyLevel > 0;
  const hasVoice = asset.voicePath != null;
  const pieceCost = getPieceCost(asset);

  const imgSrc =
    asset.assetType === 'VIDEO'
      ? getAssetUrl(getVideoPosterPath(asset.assetPath), 'small')
      : getAssetUrl(asset.assetPath, 'small');

  const handleClick = () => {
    if (!isCollected && pieceCost > 0) {
      onLockedClick(asset);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        alignItems: 'center',
        cursor: !isCollected && pieceCost > 0 ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Card */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: isIntimacyType ? '12px' : '10px',
            overflow: 'hidden',
            ...(!isIntimacyType && !isOfficialMuse && {
              background: isCollected ? (GRADE_BORDER[asset.grade]?.gradient || '#555') : '#555',
              padding: '4px',
            }),
            ...(asset.grade === 'L' && isCollected && !isIntimacyType && !isOfficialMuse && { boxShadow: GRADE_BORDER.L.shadow }),
          }}
        >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: isIntimacyType || isOfficialMuse ? '12px' : '7px',
            overflow: 'hidden',
            bgcolor: '#1a1a1a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isCollected ? (
            <Box
              component="img"
              src={imgSrc}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 50%, #2a2a2a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="icLock" size={32} color="rgba(255,255,255,0.25)" />
            </Box>
          )}
        </Box>

        {!isIntimacyType && !isOfficialMuse && isCollected && (
          <img
            src={`/assets/items/${asset.grade}.png`}
            alt=""
            style={{ position: 'absolute', top: 2, left: 2, height: 42, width: 'auto', zIndex: 2, pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
          />
        )}

        {/* 친밀도 뱃지 — 좌측 상단 */}
        {isIntimacyType && (
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              left: 5,
              zIndex: 2,
              bgcolor: intimacyLevel >= 9 ? TEMP_BG_INTIMACY_HIGH : TEMP_FG_PRIMARY_LOW,
              borderRadius: '9999px',
              px: '5px',
              py: '2px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            <Icon
              name="icHeartFilled"
              size={9}
              color={intimacyLevel >= 9 ? TEMP_FG_ACCENT : TEMP_FG_PRIMARY}
            />
            <Typography
              sx={{
                ...TEMP_TEXT_BOLD_XXS,
                fontSize: 10,
                color: intimacyLevel >= 9 ? TEMP_FG_ACCENT : TEMP_FG_PRIMARY,
                lineHeight: 1,
              }}
            >
              {intimacyLevel * 10}°C
            </Typography>
          </Box>
        )}

        {/* Media type indicators (bottom-right, for locked items) */}
        {!isCollected && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
            }}
          >
            {asset.assetType === 'VIDEO' ? (
              <Icon name="icPlayFill" size={14} color="rgba(255,255,255,0.5)" />
            ) : (
              <Icon name="icImage" size={14} color="rgba(255,255,255,0.5)" />
            )}
            {hasVoice && (
              <Icon name="icStopFill" size={14} color="rgba(255,255,255,0.5)" />
            )}
          </Box>
        )}

        {/* Video indicator for collected */}
        {asset.assetType === 'VIDEO' && isCollected && (
          <Box sx={{ position: 'absolute', top: '8%', right: '8%', zIndex: 2 }}>
            <Icon name="icPlayFill" size={16} color="rgba(255,255,255,0.8)" />
          </Box>
        )}
        </Box>
      </Box>

      {/* Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', minHeight: 20 }}>
        {isCollected ? (
          <Typography
            sx={{
              ...TEMP_TEXT_BOLD_2XS,
              color: '#FFFFFF',
              bgcolor: TEMP_FG_PRIMARY,
              borderRadius: '10px',
              px: '8px',
              py: '2px',
              fontSize: 11,
            }}
          >
            {asset.isOwned ? t('collection.acquired') : t('collection.used')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: 'rgba(255,255,255,0.4)' }}>
              {t('collection.locked')}
            </Typography>
            <Box
              component="img"
              src="/svgs/piece.svg"
              alt="piece"
              sx={{ width: 12, height: 12 }}
            />
            <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: 'rgba(255,255,255,0.4)' }}>
              {pieceCost > 0 ? pieceCost : 10}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
